/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import {
    isMotiliAccount,
    isContractorAccount,
    isClientAccount,
    getAccountRoles,
} from 'common/services/AuthService';
import { del, post } from 'common/services/ApiService';
import Alert from 'common/components/Alert';
import { LINE_ITEM_STATUSES } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import * as logger from 'common/utils/Logger';
import { bulkRemoveLineItems } from 'common/services/WorkOrderService';
import { deleteLineItems } from 'common/services/QuoteService';
import { filterApproveAll, filterRemoveAll } from '../utils/line-item-utils';

/**
 * lineItemsEditable
 * @param {Array} statuses
 * @param {string} statusId

 * @returns {Boolean}
 */
export function lineItemsEditable(statuses, statusId) {
    if (!statuses) {
        return false;
    }

    const status = statuses.find(s => s.id === statusId);
    if (!status) {
        logger.error(
            `LineItemService#lineItemsEditable invoked without valid status argument. Status ${statusId} provided`
        );
        return false;
    }

    const roles = getAccountRoles();
    if (isMotiliAccount()) {
        for (let i = 0; i < roles.length; i += 1) {
            const role = roles[i];
            if (role && status.lineItemsEditable.motili[role]) {
                return true;
            }
        }
        return false;
    }
    if (isContractorAccount()) {
        for (let i = 0; i < roles.length; i += 1) {
            const role = roles[i];
            if (role && status.lineItemsEditable.contractor[role]) {
                return true;
            }
        }
        return false;
    }
    if (isClientAccount()) {
        for (let i = 0; i < roles.length; i += 1) {
            const role = roles[i];
            if (role && status.lineItemsEditable.client[role]) {
                return true;
            }
        }
        return false;
    }
    return false;
}

export function hasOrderedItems(lineItems) {
    return lineItems.findIndex(l => isOrderedItem(l)) > -1;
}

export function isOrderedItem(lineItem) {
    if (lineItem.productCategoryId === 'EQUIPMENT') {
        return true;
    }
    if (lineItem.productCategoryId === 'PART') {
        return true;
    }
    if (lineItem.productCategoryId === 'SHIPPING') {
        return true;
    }
    if (lineItem.productCategoryId === 'CUSTOM') {
        if (lineItem.productSubCategoryId === 'EQUIPMENT') {
            return true;
        }
        if (lineItem.productSubCategoryId === 'PART') {
            return true;
        }
        if (lineItem.productSubCategoryId === 'SHIPPING') {
            return true;
        }
    }
    if (_hasMigratedOrderedItem(lineItem)) {
        return true;
    }
    return false;
}

function _hasMigratedOrderedItem(lineItem) {
    if (lineItem.productCategoryId === 'MIGRATED') {
        let _notOrderedLineItem = lineItem.sku.substring(0, 4) === 'SRV-';
        if (_notOrderedLineItem) return false;
        _notOrderedLineItem = lineItem.sku.substring(0, 4) === 'SKU-';
        if (_notOrderedLineItem) return false;
        _notOrderedLineItem = lineItem.sku.substring(0, 6) === 'HVCOM-';
        if (_notOrderedLineItem) return false;
        _notOrderedLineItem = lineItem.sku.substring(0, 6) === 'HVAC-';
        if (_notOrderedLineItem) return false;
        _notOrderedLineItem = lineItem.sku.match(/\d\./);
        if (_notOrderedLineItem) return false;
        return true;
    }
    return false;
}

export function hasValidSerialNumberEditCategory(lineItem) {
    return (
        ['EQUIPMENT', 'PART'].includes(lineItem.productCategoryId) ||
        (['SUPPLIED', 'CUSTOM'].includes(lineItem.productCategoryId) &&
            ['EQUIPMENT', 'PART'].includes(lineItem.productSubCategoryId))
    );
}

export function hasValidSerialNumberEditOrderStatus(orderStatusId) {
    return [
        'CREATED',
        'SUBMITTED',
        'ORDERED',
        'ORDERED_CONFIRM',
        'COMPLETED',
        'RECONCILING',
        'CLOSED',
        'HOLD',
    ].some(orderStatus => orderStatus === orderStatusId);
}

export const approveAllLineItems = async ({
    documentType,
    documentId,
    lineItems,
    handleUpdateAfterAllHandler,
    reason,
    closeTheModal,
}) => {
    try {
        const promises = filterApproveAll(lineItems).map(li =>
            post(
                true,
                `${documentType}/${documentId}/lineItems/${li.id}/approve?reason=${reason}`
            )
        );
        if (!promises || promises.length === 0) {
            Alert.warning('No Line Items available for this update.');
            closeTheModal();
            return;
        }
        await Promise.all(promises);
        await handleUpdateAfterAllHandler('approveAll');
        Alert.success('Successfully approved all line items!');
    } catch (error) {
        Alert.error(error.message);
    }
    closeTheModal();
};

export const removeAllLineItems = async ({
    documentType,
    documentId,
    lineItems,
    handleUpdateAfterAllHandler,
    closeTheModal,
}) => {
    if (!documentType || !documentId || lineItems.length < 1) {
        return Alert.error(
            'required parameters are missing from the removeAllLineItems invocation!'
        );
    }

    try {
        const lineItemsForRemoval = filterRemoveAll(lineItems);

        if (!lineItemsForRemoval.length) {
            Alert.warning('No Line Items available for this update.');
            closeTheModal();
            return;
        }

        if (documentType !== 'orders' && documentType !== 'quotes') {
            const lineItemsForUnapproval = lineItemsForRemoval.filter(
                li => li.statusId === LINE_ITEM_STATUSES.APPROVED
            );
            const unapprovalPromises = lineItemsForUnapproval.map(li =>
                post(
                    true,
                    `${documentType}/${documentId}/lineItems/${li.id}/unapprove?reason=${'Remove All invoked.'}`
                )
            );

            await Promise.all(unapprovalPromises);
        }

        const lineItemIds = lineItemsForRemoval.map(li => li.id);

        if (documentType === 'workOrders') {
            await bulkRemoveLineItems(documentId, lineItemIds);
        } else if (documentType === 'quotes') {
            await deleteLineItems(documentId, lineItemIds);
        } else {
            const removalPromises = lineItemsForRemoval.map(li =>
                del(`${documentType}/${documentId}/lineItems/${li.id}`)
            );
            await Promise.all(removalPromises);
        }

        await handleUpdateAfterAllHandler();
        Alert.success('Successfully removed all line items!');
    } catch (error) {
        Alert.error(error.message);
    } finally {
        closeTheModal();
    }
};
